/** Zdroj zvuku – hotová URL, nebo lazy import, aby se mp3 stáhlo až při prvním přehrání. */
export type SoundSource = string | (() => Promise<string>)

export type SoundType = 'order' | 'beep'

/**
 * Registr zvuků, který hlídá, že hraje vždy jen jeden – další přehrání se zahodí,
 * dokud předchozí nedohraje (nebo se s `interrupt` běžící zvuk ukončí). Bez toho
 * se při dávce notifikací zvuky pouští přes sebe.
 *
 * Vlastní zvuk se přidá přes `register(type, source)`, typ je pak klíč pro `playMusic()`.
 */
class SoundLibrary {
  private sources = new Map<string, SoundSource>()
  private urls = new Map<string, string>()
  private audio: HTMLAudioElement | null = null
  private playingType: string | null = null

  register(type: string, source: SoundSource) {
    this.sources.set(type, source)
    this.urls.delete(type)
    return this
  }

  has(type: string) {
    return this.sources.has(type)
  }

  /** Typ právě hrajícího zvuku, jinak null. */
  get playing() {
    return this.playingType
  }

  /** Bez typu zastaví cokoli, s typem jen ten svůj – aby cizí stop nezabil jiný zvuk. */
  stop(type?: string) {
    if (type && this.playingType !== type) return
    this.audio?.pause()
    this.audio = null
    this.playingType = null
  }

  async play(
    type: string,
    { interrupt = false, loop = false }: { interrupt?: boolean; loop?: boolean } = {}
  ) {
    // Stejný zvuk už hraje – nerestartovat, jinak by se smyčka pořád sekala od začátku.
    if (this.playingType === type) return
    if (this.playingType) {
      if (!interrupt) return
      this.stop()
    }
    // Nastaveno synchronně, ještě před await – jinak by dvě rychlá volání prošla obě.
    this.playingType = type

    try {
      const url = await this.resolve(type)
      // Během načítání mohl zvuk přebít někdo jiný přes `interrupt`.
      if (this.playingType !== type) return

      const audio = new Audio(url)
      audio.loop = loop
      const release = () => {
        if (this.audio === audio) this.stop()
      }
      // Smyčka končí výhradně přes `stop()`, `ended` u ní stejně nepřijde.
      audio.addEventListener('ended', release)
      audio.addEventListener('error', release)
      this.audio = audio
      await audio.play()
    } catch (e) {
      if (this.playingType === type) this.stop()
      console.warn(`Zvuk "${type}" se nepodařilo přehrát`, e)
    }
  }

  private async resolve(type: string) {
    const cached = this.urls.get(type)
    if (cached) return cached
    const source = this.sources.get(type)
    if (!source) throw new Error(`Zvuk "${type}" není registrovaný`)
    const url = typeof source === 'string' ? source : await source()
    this.urls.set(type, url)
    return url
  }
}

const library = new SoundLibrary()
  .register('order', () =>
    import('@@/shared-components/sounds/newOrder.mp3').then((module) => module.default)
  )
  .register('beep', () =>
    import('@@/shared-components/sounds/beep.mp3').then((module) => module.default)
  )

export const useMusic = () => {
  const canPlay = () => {
    if (!import.meta.client) return false
    // Prohlížeč pustí zvuk až po interakci uživatele (Safari userActivation nezná).
    if (navigator.userActivation && !navigator.userActivation.hasBeenActive) {
      console.warn('The user did not make any interaction')
      return false
    }
    return true
  }

  /** Neznámý typ (např. klíč FCM notifikace) spadne na výchozí zvuk nové objednávky. */
  const playMusic = async (
    type?: SoundType | string,
    options?: { interrupt?: boolean; loop?: boolean }
  ) => {
    if (!canPlay()) return
    await library.play(type && library.has(type) ? type : 'order', options)
  }

  /** Registrace vlastního zvuku pod vlastním typem – přepíše i výchozí zvuky. */
  const registerSound = (type: string, source: SoundSource) => {
    library.register(type, source)
  }

  const stopMusic = (type?: SoundType | string) => library.stop(type)

  const playingSound = () => library.playing

  return { playMusic, registerSound, stopMusic, playingSound }
}
